import type { getDefaultStore } from 'jotai'

import { ORDER_FORM_SHEET_HEIGHT } from '@/features/order/order-form/components/order-form-sheet'
import { getSelectedInstrumentRowKey } from '@/features/order/order-form/lib/selected-instrument-identity'
import { orderGroupTicketId } from '@/lib/resolve-order-form-target'
import { activePageIdAtom } from '@/state/dashboard-layouts'
import type { LinkingColor } from '@/state/linking'
import {
  DEFAULT_ORDER_FORM_CARD_ID,
  orderFormPopupAnchorAtom,
  orderFormPopupCardIdAtom,
  orderFormPopupFormAtom,
  type OrderFormSelection,
  orderFormSelectionAtomFamily,
  registeredOrderContainersAtom,
} from '@/state/order'

type JotaiStore = ReturnType<typeof getDefaultStore>

const GROUP_COLORS: Exclude<LinkingColor, 'unlinked'>[] = [
  'mint',
  'yellow',
  'orange',
  'pink',
  'purple',
  'blue',
  'green',
]

/**
 * Every group's rows in one selection, deduped by (row, account) — the same identity the
 * form keys rows by, so a symbol on two accounts stays two rows.
 */
export function mergeOrderFormSelections(selections: OrderFormSelection[]): OrderFormSelection {
  const instruments = new Map<string, OrderFormSelection['selectedInstruments'][number]>()
  const options = new Map<string, OrderFormSelection['selectedOptions'][number]>()
  for (const selection of selections) {
    for (const item of selection.selectedInstruments) {
      const key = `${getSelectedInstrumentRowKey(item)}:${item.accountId ?? ''}`
      if (!instruments.has(key)) instruments.set(key, item)
    }
    for (const item of selection.selectedOptions) {
      const key = `${item.instrumentName}:${item.accountId ?? ''}`
      if (!options.has(key)) options.set(key, item)
    }
  }
  return { selectedInstruments: [...instruments.values()], selectedOptions: [...options.values()] }
}

/**
 * Opens the Order Form sheet (the O shortcut / status bar "Open order form"). With no Order
 * Form widget on the page it is the form, as before. With widgets, it opens anyway on its own
 * ticket, filled with a COPY of every group's rows — each widget's and each parked group
 * ticket's (see orderGroupTicketId). The widgets keep their rows; nothing is moved.
 */
export function openOrderFormSheet(store: JotaiStore, lastFocusedCard: string | null) {
  store.set(orderFormPopupAnchorAtom, {
    right: 8,
    left: 0,
    top: window.innerHeight - ORDER_FORM_SHEET_HEIGHT - 48,
    bottom: window.innerHeight - 48,
  })
  const registered = store.get(registeredOrderContainersAtom)
  if (registered.size === 0) {
    store.set(orderFormPopupCardIdAtom, lastFocusedCard ?? DEFAULT_ORDER_FORM_CARD_ID)
    return
  }

  // Same page key DashboardLayout gives LinkingPageContext.
  const pageId = store.get(activePageIdAtom) ?? 'default'
  const sources = [
    DEFAULT_ORDER_FORM_CARD_ID,
    ...registered,
    ...GROUP_COLORS.map((color) => orderGroupTicketId(pageId, color)),
  ]
  store.set(
    orderFormSelectionAtomFamily(DEFAULT_ORDER_FORM_CARD_ID),
    mergeOrderFormSelections(sources.map((id) => store.get(orderFormSelectionAtomFamily(id))))
  )
  store.set(orderFormPopupFormAtom, null)
  store.set(orderFormPopupCardIdAtom, DEFAULT_ORDER_FORM_CARD_ID)
}
